import React, { useEffect, useMemo, useRef, useState } from "react";
import {
	Box,
	Typography,
	TextField,
	Button,
	MenuItem,
	Select,
	InputLabel,
	FormControl,
	useMediaQuery,
	useTheme,
	InputAdornment,
	CircularProgress,
} from "@mui/material";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import PrintOutlinedIcon from "@mui/icons-material/PrintOutlined";
import LockRoundedIcon from "@mui/icons-material/LockRounded";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import WorkspacePremiumIcon from "@mui/icons-material/WorkspacePremium";
import { BasicModal } from "../BasicModal/BasicModal";
import PackConfirmModal from "../PackConfirmModal";
import {
	DEFAULT_COUNTRY_ID,
	getCustomStickerCountries,
	getCustomStickerCountry,
} from "../../data/customStickers";
import StickerPreview from "./StickerPreview";
import { formatStickerText } from "./fitText";
import { generateStickerPdf } from "./generateStickerPdf";
import { useFirebase } from "../../context/ApiContext";

/**
 * Modal de stickers personalizados.
 *
 * Flujo:
 *  1) El usuario elige país (por ahora solo Japón).
 *  2) Ve la rejilla de stickers disponibles para ese país.
 *  3) Al seleccionar uno, ve una preview grande y puede escribir su nombre.
 *  4) Al pulsar "Añadir", se notifica al padre con la config necesaria para
 *     insertar el sticker en el canvas.
 *
 * El padre recibe un objeto con la forma:
 *   {
 *     baseImage: string,   // URL autenticada de la imagen base (backend)
 *     aspectRatio: number,
 *     customText: {
 *       value: string,     // texto que el usuario escribió
 *       config: object,    // bloque `text` del sticker (posición, estilo, etc.)
 *     },
 *     meta: { countryId, stickerId, title }
 *   }
 */
const CustomStickerModal = ({ open, onClose, onAdd }) => {
	const theme = useTheme();
	const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
	const {
		user,
		hasCustomStickersEntitlement,
		getPacks,
		purchasePack,
		getCustomStickerAssetUrl,
	} = useFirebase();

	const countries = useMemo(() => getCustomStickerCountries(), []);
	const [countryId, setCountryId] = useState(DEFAULT_COUNTRY_ID);
	const country = useMemo(
		() => getCustomStickerCountry(countryId),
		[countryId],
	);

	// Paywall: el pack que desbloquea los stickers de este país.
	// Se resuelve vía /api/packs buscando el que declara `countryId` en sus
	// entitlements.customStickerCountries. Se cachea por país.
	const [packsForCountry, setPacksForCountry] = useState({});
	const [purchasing, setPurchasing] = useState(false);

	const isLocked = !hasCustomStickersEntitlement?.(countryId);
	const unlockPack = packsForCountry[countryId] || null;

	useEffect(() => {
		if (!open || !isLocked) return;
		if (packsForCountry[countryId] !== undefined) return;
		let cancelled = false;
		(async () => {
			try {
				const packs = (await getPacks?.()) || [];
				const match = packs.find((p) =>
					p?.entitlements?.customStickerCountries?.includes(countryId),
				);
				if (!cancelled) {
					setPacksForCountry((prev) => ({
						...prev,
						[countryId]: match || null,
					}));
				}
			} catch {
				if (!cancelled) {
					setPacksForCountry((prev) => ({ ...prev, [countryId]: null }));
				}
			}
		})();
		return () => {
			cancelled = true;
		};
	}, [open, isLocked, countryId, getPacks, packsForCountry]);

	const [showPackConfirm, setShowPackConfirm] = useState(false);

	const handleUnlock = () => {
		if (!unlockPack) return;
		if (!user) {
			onClose?.();
			return;
		}
		setShowPackConfirm(true);
	};

	const handleConfirmPackPurchase = async () => {
		setShowPackConfirm(false);
		setPurchasing(true);
		try {
			await purchasePack?.(unlockPack.id);
		} catch (err) {
			console.error("Error purchasing pack from stickers modal:", err);
		} finally {
			setPurchasing(false);
		}
	};

	const formatPackPrice = (pack) => {
		if (!pack) return "";
		try {
			return new Intl.NumberFormat("es-ES", {
				style: "currency",
				currency: pack.currency || "EUR",
				minimumFractionDigits: 2,
				maximumFractionDigits: 2,
			}).format(pack.price);
		} catch {
			return `${Number(pack.price).toFixed(2)} €`;
		}
	};

	const resolvedStickers = useMemo(() => {
		if (!country?.stickers) return [];
		return country.stickers.map((s) => ({
			...s,
			baseImage: getCustomStickerAssetUrl(country.id, s.baseImage),
		}));
	}, [country, getCustomStickerAssetUrl]);

	const [selectedStickerId, setSelectedStickerId] = useState(
		country?.stickers?.[0]?.id || null,
	);
	const [name, setName] = useState("");
	const nameInputRef = useRef(null);

	useEffect(() => {
		if (!open) return;
		setCountryId(DEFAULT_COUNTRY_ID);
		const firstCountry = getCustomStickerCountry(DEFAULT_COUNTRY_ID);
		setSelectedStickerId(firstCountry?.stickers?.[0]?.id || null);
		setName("");
	}, [open]);

	useEffect(() => {
		if (!country) return;
		if (!resolvedStickers.find((s) => s.id === selectedStickerId)) {
			setSelectedStickerId(resolvedStickers[0]?.id || null);
		}
	}, [country, resolvedStickers, selectedStickerId]);

	const selectedSticker = useMemo(
		() => resolvedStickers.find((s) => s.id === selectedStickerId) || null,
		[resolvedStickers, selectedStickerId],
	);

	const maxChars = selectedSticker?.text?.maxChars ?? 20;
	const trimmedName = name.trim();
	const canAdd = !!selectedSticker && !isLocked;

	const [pdfLoading, setPdfLoading] = useState(false);

	const handleDownloadPdf = async () => {
		if (!resolvedStickers.length) return;
		if (isLocked) return;
		setPdfLoading(true);
		try {
			const slug = trimmedName
				? trimmedName.toLowerCase().replace(/\s+/g, "-")
				: "sin-nombre";
			await generateStickerPdf({
				stickers: resolvedStickers,
				name: trimmedName,
				title: `Stickers de ${country.name}${
					trimmedName ? ` · ${trimmedName}` : ""
				}`,
				fileName: `stickers-${country.id}-${slug}.pdf`,
				copiesPerSticker: 1,
			});
		} catch (err) {
			console.error("Error generando PDF de stickers:", err);
		} finally {
			setPdfLoading(false);
		}
	};

	const handleAdd = () => {
		if (!selectedSticker) return;
		if (isLocked) return;
		const finalValue = formatStickerText(trimmedName, selectedSticker.text);
		onAdd?.({
			baseImage: selectedSticker.baseImage,
			aspectRatio: selectedSticker.aspectRatio || 1,
			customText: {
				value: finalValue,
				isDefault: trimmedName === "",
				config: selectedSticker.text,
			},
			meta: {
				countryId: country.id,
				stickerId: selectedSticker.id,
				title: selectedSticker.title,
			},
		});
		onClose?.();
	};

	/* ── Fragmentos reutilizables para ambos layouts ── */

	const headerBlock = (
		<>
			<Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mb: 0.25 }}>
				<AutoAwesomeIcon sx={{ color: "#6366F1", fontSize: isMobile ? 16 : 20 }} />
				<Typography
					sx={{
						fontSize: 11,
						fontWeight: 700,
						textTransform: "uppercase",
						letterSpacing: "0.08em",
						color: "#6366F1",
					}}
				>
					Nuevo
				</Typography>
			</Box>
			<Typography
				variant="h5"
				sx={{
					fontSize: isMobile ? 18 : 24,
					fontWeight: 700,
					color: "#0f172a",
					mb: 0.25,
				}}
			>
				Stickers personalizados
			</Typography>
			<Typography
				sx={{
					fontSize: isMobile ? 12 : 13,
					color: "#64748b",
					mb: isMobile ? 1.5 : 2.5,
					pr: isMobile ? 3 : 4,
					lineHeight: 1.4,
				}}
			>
				Elige un sticker y escribe tu nombre. Se integrará dentro del
				diseño al instante.
			</Typography>
		</>
	);

	const countrySelect = (
		<FormControl fullWidth size="small" sx={{ mb: isMobile ? 1.5 : 2.5 }}>
			<InputLabel id="custom-sticker-country-label">País</InputLabel>
			<Select
				labelId="custom-sticker-country-label"
				value={countryId}
				label="País"
				onChange={(e) => setCountryId(e.target.value)}
				MenuProps={{ sx: { zIndex: 10003 } }}
			>
				{countries.map((c) => (
					<MenuItem key={c.id} value={c.id}>
						<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
							<span style={{ fontSize: 18 }}>{c.flag}</span>
							<span>{c.name}</span>
							<Typography
								component="span"
								sx={{ color: "#94a3b8", fontSize: 12, ml: 0.5 }}
							>
								· {c.stickerCount}
							</Typography>
						</Box>
					</MenuItem>
				))}
			</Select>
		</FormControl>
	);

	const previewBlock = (
		<Box
			sx={{
				display: "flex",
				alignItems: "center",
				justifyContent: "center",
				backgroundColor: "#f8fafc",
				borderRadius: 2.5,
				p: isMobile ? 1.5 : 3,
				minHeight: isMobile ? 140 : 300,
				flex: isMobile ? "0 0 auto" : 1,
			}}
		>
			{selectedSticker ? (
				<Box
					sx={{
						width: "100%",
						maxWidth: isMobile ? 180 : 320,
						aspectRatio: `1 / ${selectedSticker.aspectRatio || 1}`,
					}}
				>
					<StickerPreview
						sticker={selectedSticker}
						value={trimmedName}
						size="fill"
					/>
				</Box>
			) : (
				<Typography sx={{ color: "#94a3b8", fontSize: 13 }}>
					Selecciona un sticker para previsualizarlo.
				</Typography>
			)}
		</Box>
	);

	const inputBlock = (
		<Box>
			<TextField
				inputRef={nameInputRef}
				label="Tu nombre"
				placeholder={
					selectedSticker?.text?.placeholder || "Escribe tu nombre"
				}
				value={name}
				onChange={(e) => setName(e.target.value.slice(0, maxChars))}
				fullWidth
				size="small"
				autoFocus={!isMobile}
				InputProps={{
					endAdornment: (
						<InputAdornment position="end">
							<Typography
								sx={{
									fontSize: 11,
									color: name.length >= maxChars ? "#ef4444" : "#94a3b8",
									fontVariantNumeric: "tabular-nums",
								}}
							>
								{name.length}/{maxChars}
							</Typography>
						</InputAdornment>
					),
				}}
			/>
			<Typography sx={{ fontSize: 11, color: "#94a3b8", mt: 0.5 }}>
				El tamaño se ajusta automáticamente para que quepa en el sticker.
			</Typography>
		</Box>
	);

	const pdfButton = (
		<Button
			onClick={handleDownloadPdf}
			disabled={pdfLoading || !country?.stickers?.length}
			startIcon={
				pdfLoading ? (
					<CircularProgress size={14} thickness={5} />
				) : (
					<PrintOutlinedIcon sx={{ fontSize: 18 }} />
				)
			}
			sx={{
				textTransform: "none",
				fontWeight: 600,
				fontSize: isMobile ? 12 : 13,
				color: "#4f46e5",
				border: "1px dashed #c7d2fe",
				borderRadius: 2,
				justifyContent: "flex-start",
				px: 1.5,
				py: isMobile ? 0.75 : 1,
				"&:hover": { backgroundColor: "#eef2ff", borderStyle: "solid" },
			}}
		>
			{pdfLoading
				? "Preparando PDF…"
				: `Descargar PDF para imprimir (${country?.stickers?.length || 0} stickers)`}
		</Button>
	);

	const actionButtons = (
		<Box
			sx={{
				display: "flex",
				gap: 1,
				justifyContent: "flex-end",
				mt: isMobile ? 0 : 0.5,
			}}
		>
			<Button
				onClick={onClose}
				sx={{
					color: "#64748b",
					textTransform: "none",
					fontWeight: 600,
					fontSize: isMobile ? 13 : 14,
				}}
			>
				Cancelar
			</Button>
			<Button
				variant="contained"
				onClick={handleAdd}
				disabled={!canAdd}
				sx={{
					textTransform: "none",
					fontWeight: 700,
					borderRadius: 2,
					px: isMobile ? 2.5 : 3,
					fontSize: isMobile ? 13 : 14,
					background: "linear-gradient(135deg, #6366F1 0%, #8B5CF6 100%)",
					boxShadow: "0 6px 16px rgba(99,102,241,0.25)",
					"&:hover": {
						background: "linear-gradient(135deg, #5558E3 0%, #7C4FE0 100%)",
					},
				}}
			>
				Añadir a la guía
			</Button>
		</Box>
	);

	const stickerGrid = (
		<>
			<Typography
				sx={{
					fontSize: isMobile ? 11 : 12,
					fontWeight: 700,
					textTransform: "uppercase",
					letterSpacing: "0.06em",
					color: "#64748b",
					mb: 0.75,
				}}
			>
				Stickers de {country?.name}
			</Typography>
			<Box
				sx={{
					display: "grid",
					gridTemplateColumns: "repeat(3, 1fr)",
					gap: isMobile ? 1 : 1.5,
					pb: 1,
				}}
			>
				{resolvedStickers.map((sticker) => (
					<Box
						key={sticker.id}
						onClick={() => setSelectedStickerId(sticker.id)}
						sx={{
							backgroundColor: "#ffffff",
							borderRadius: isMobile ? 1.5 : 2,
							p: isMobile ? 0.75 : 1,
							cursor: "pointer",
							border:
								selectedStickerId === sticker.id
									? "2px solid #6366F1"
									: "2px solid #eef0f4",
							transition: "border-color 0.15s ease",
						}}
					>
						<StickerPreview
							sticker={sticker}
							value={trimmedName}
							size="fill"
							interactive
							selected={false}
							onClick={() => setSelectedStickerId(sticker.id)}
							ariaLabel={`Seleccionar ${sticker.title}`}
						/>
						<Typography
							sx={{
								fontSize: isMobile ? 10 : 11,
								fontWeight: 600,
								color:
									selectedStickerId === sticker.id
										? "#4f46e5"
										: "#475569",
								textAlign: "center",
								mt: 0.25,
								lineHeight: 1.2,
								whiteSpace: "nowrap",
								overflow: "hidden",
								textOverflow: "ellipsis",
							}}
						>
							{sticker.title}
						</Typography>
					</Box>
				))}
			</Box>
		</>
	);

	const paywallOverlay = isLocked ? (
		<Box
			sx={{
				position: "absolute",
				inset: 0,
				zIndex: 3,
				backdropFilter: "blur(6px)",
				WebkitBackdropFilter: "blur(6px)",
				backgroundColor: "rgba(255,255,255,0.72)",
				display: "flex",
				alignItems: "center",
				justifyContent: "center",
				p: isMobile ? 2 : 3.5,
			}}
		>
			<Box
				sx={{
					maxWidth: 360,
					width: "100%",
					textAlign: "center",
					background: "#ffffff",
					border: "1px solid #eef0f4",
					borderRadius: 3,
					p: isMobile ? 2.5 : 3,
					boxShadow: "0 20px 40px -20px rgba(15,23,42,0.25)",
				}}
			>
				<Box
					sx={{
						width: 44,
						height: 44,
						borderRadius: "50%",
						display: "inline-flex",
						alignItems: "center",
						justifyContent: "center",
						backgroundColor: "#eef2ff",
						color: "#6366F1",
						mb: 1.5,
					}}
				>
					<LockRoundedIcon sx={{ fontSize: 22 }} />
				</Box>
				<Typography
					sx={{
						fontSize: 11,
						fontWeight: 700,
						textTransform: "uppercase",
						letterSpacing: "0.08em",
						color: "#6366F1",
						mb: 0.5,
					}}
				>
					Contenido bloqueado
				</Typography>
				<Typography
					variant="h6"
					sx={{
						fontSize: isMobile ? 16 : 18,
						fontWeight: 800,
						color: "#0f172a",
						mb: 0.75,
						letterSpacing: "-0.01em",
					}}
				>
					{unlockPack?.title
						? `Desbloquea con ${unlockPack.title}`
						: "Desbloquea los stickers"}
				</Typography>
				<Typography sx={{ fontSize: 13, color: "#64748b", mb: 2 }}>
					Los stickers personalizables de {country?.flag}{" "}
					{country?.name} están incluidos en este pack, junto con la
					guía completa y páginas extra.
				</Typography>

			{unlockPack ? (
				<Button
					variant="contained"
					fullWidth
					onClick={handleUnlock}
					disabled={purchasing || !unlockPack.isAvailableForPurchase}
					endIcon={
						purchasing ? (
							<CircularProgress size={14} sx={{ color: "#fff" }} />
						) : (
							<ArrowForwardRoundedIcon />
						)
					}
					sx={{
						textTransform: "none",
						fontWeight: 700,
						fontSize: isMobile ? 13 : 14,
						borderRadius: 2,
						py: 1.25,
						background:
							"linear-gradient(135deg, #6366F1 0%, #8B5CF6 100%)",
						boxShadow: "0 10px 20px -8px rgba(99,102,241,0.45)",
						"&:hover": {
							background:
								"linear-gradient(135deg, #5558E3 0%, #7C4FE0 100%)",
						},
					}}
				>
					{user
						? `Desbloquea con ${unlockPack.title} · ${formatPackPrice(unlockPack)}`
						: `Regístrate y consíguelo · ${formatPackPrice(unlockPack)}`}
				</Button>
			) : (
				<Button variant="contained" fullWidth disabled>
					No disponible por ahora
				</Button>
			)}

			<Typography
				sx={{
					mt: 1,
					fontSize: 11,
					color: "#94a3b8",
					display: "inline-flex",
					alignItems: "center",
					gap: 0.5,
				}}
			>
				<WorkspacePremiumIcon sx={{ fontSize: 14 }} />
				También incluido en tu plan Pro
			</Typography>
			</Box>
		</Box>
	) : null;

	const packConfirmEl = (
		<PackConfirmModal
			open={showPackConfirm}
			onClose={() => setShowPackConfirm(false)}
			onConfirm={handleConfirmPackPurchase}
			loading={purchasing}
			packTitle={unlockPack?.title || "Pack"}
			packPrice={formatPackPrice(unlockPack)}
		/>
	);

	/* ── Layout móvil: columna única scrollable ── */
	if (isMobile) {
		return (
			<>
				<BasicModal
					showModal={open}
					handleClose={onClose}
					width={920}
					noPadding
					contentSx={{
						bgcolor: "#ffffff",
						borderRadius: 2.5,
						overflow: "hidden",
					}}
				>
					<Box
						sx={{
							position: "relative",
							maxHeight: "calc(100dvh - 48px)",
							overflowY: "auto",
							WebkitOverflowScrolling: "touch",
						}}
					>
						{paywallOverlay}

						{/* Cabecera + país */}
						<Box sx={{ px: 2, pt: 2, pb: 0, backgroundColor: "#fafbff" }}>
							{headerBlock}
							{countrySelect}
						</Box>

						{/* Preview + input + botones */}
						<Box
							sx={{
								px: 2,
								py: 1.5,
								display: "flex",
								flexDirection: "column",
								gap: 1.25,
								backgroundColor: "#ffffff",
							}}
						>
							{previewBlock}
							{inputBlock}
							{pdfButton}
							{actionButtons}
						</Box>

						{/* Rejilla de stickers */}
						<Box
							sx={{
								px: 2,
								pt: 1.5,
								pb: 2,
								backgroundColor: "#fafbff",
								borderTop: "1px solid #eef0f4",
							}}
						>
							{stickerGrid}
						</Box>
					</Box>
				</BasicModal>
				{packConfirmEl}
			</>
		);
	}

	/* ── Layout desktop: dos paneles lado a lado ── */
	return (
		<>
		<BasicModal
			showModal={open}
			handleClose={onClose}
			width={920}
			noPadding
			contentSx={{
				bgcolor: "#ffffff",
				borderRadius: 3,
				overflow: "hidden",
			}}
		>
			<Box
				sx={{
					display: "flex",
					flexDirection: "row",
					height: "min(640px, calc(100vh - 125px))",
				}}
			>
				{/* Panel izquierdo */}
				<Box
					sx={{
						flex: "1 1 55%",
						minHeight: 0,
						minWidth: 0,
						display: "flex",
						flexDirection: "column",
						p: 3.5,
						pb: 2,
						borderRight: "1px solid #eef0f4",
						backgroundColor: "#fafbff",
					}}
				>
					{headerBlock}
					{countrySelect}

					<Typography
						sx={{
							fontSize: 12,
							fontWeight: 700,
							textTransform: "uppercase",
							letterSpacing: "0.06em",
							color: "#64748b",
							mb: 0.75,
						}}
					>
						Stickers de {country?.name}
					</Typography>

					<Box
						sx={{
							flex: "1 1 auto",
							minHeight: 0,
							overflowY: "auto",
							pr: 0.5,
							mr: -0.5,
							"&::-webkit-scrollbar": { width: 8 },
							"&::-webkit-scrollbar-thumb": {
								backgroundColor: "#d6dbe7",
								borderRadius: 4,
							},
							"&::-webkit-scrollbar-thumb:hover": {
								backgroundColor: "#b8bfcf",
							},
						}}
					>
						<Box
							sx={{
								display: "grid",
								gridTemplateColumns: "repeat(3, 1fr)",
								gap: 1.5,
								pb: 1,
							}}
						>
							{resolvedStickers.map((sticker) => (
								<Box
									key={sticker.id}
									onClick={() => setSelectedStickerId(sticker.id)}
									sx={{
										backgroundColor: "#ffffff",
										borderRadius: 2,
										p: 1,
										cursor: "pointer",
										border:
											selectedStickerId === sticker.id
												? "2px solid #6366F1"
												: "2px solid #eef0f4",
										transition: "border-color 0.15s ease",
									}}
								>
									<StickerPreview
										sticker={sticker}
										value={trimmedName}
										size="fill"
										interactive
										selected={false}
										onClick={() => setSelectedStickerId(sticker.id)}
										ariaLabel={`Seleccionar ${sticker.title}`}
									/>
									<Typography
										sx={{
											fontSize: 11,
											fontWeight: 600,
											color:
												selectedStickerId === sticker.id
													? "#4f46e5"
													: "#475569",
											textAlign: "center",
											mt: 0.5,
											whiteSpace: "nowrap",
											overflow: "hidden",
											textOverflow: "ellipsis",
										}}
									>
										{sticker.title}
									</Typography>
								</Box>
							))}
						</Box>
					</Box>
				</Box>

				{/* Panel derecho */}
				<Box
					sx={{
						position: "relative",
						flex: "1 1 45%",
						minHeight: 0,
						minWidth: 0,
						p: 3.5,
						display: "flex",
						flexDirection: "column",
						gap: 2,
						backgroundColor: "#ffffff",
						overflowY: "auto",
					}}
				>
					{paywallOverlay}
					{previewBlock}
					{inputBlock}
					{pdfButton}
					<Box
						sx={{
							display: "flex",
							gap: 1,
							justifyContent: "flex-end",
							mt: 0.5,
						}}
					>
						<Button
							onClick={onClose}
							sx={{
								color: "#64748b",
								textTransform: "none",
								fontWeight: 600,
							}}
						>
							Cancelar
						</Button>
						<Button
							variant="contained"
							onClick={handleAdd}
							disabled={!canAdd}
							sx={{
								textTransform: "none",
								fontWeight: 700,
								borderRadius: 2,
								px: 3,
								background:
									"linear-gradient(135deg, #6366F1 0%, #8B5CF6 100%)",
								boxShadow: "0 6px 16px rgba(99,102,241,0.25)",
								"&:hover": {
									background:
										"linear-gradient(135deg, #5558E3 0%, #7C4FE0 100%)",
								},
							}}
						>
							Añadir a la guía
						</Button>
					</Box>
				</Box>
			</Box>
		</BasicModal>
	{packConfirmEl}
	</>
	);
};

export default CustomStickerModal;
